/**
 * WhatsNewModal Component (DD-160)
 *
 * Glassmorphic modal that displays version-keyed feature announcements
 * to returning users who have been inactive for >7 days.
 *
 * Design:
 * - Dark overlay with translucent glass card
 * - Feature cards with category-based accent colors
 * - Tappable cards for deep linking into features
 * - Dismiss button at the bottom
 *
 * Follows SafetyModalsContext pattern: single modal at a time,
 * controlled by parent hook (useReEngagement).
 *
 * Accessibility:
 * - 44px minimum tap targets
 * - High contrast text
 * - Semantic testIDs for E2E
 */

import React from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import {
  WHATS_NEW_RELEASES,
  getLatestVersion,
  type WhatsNewItem,
} from '../lib/whats-new-content';

/** Category accent colors */
const CATEGORY_COLORS: Record<WhatsNewItem['category'], string> = {
  feature: '#8B5CF6',    // Purple
  improvement: '#06B6D4', // Cyan
  fix: '#10B981',         // Emerald
};


interface WhatsNewModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Days since last activity (shown in subtitle) */
  daysInactive: number;
  /** Called when user dismisses the modal */
  onDismiss: () => void;
  /** Called when user taps a feature card */
  onFeatureTap: (featureTitle: string, route?: string) => void;
}

export function WhatsNewModal({
  visible,
  daysInactive,
  onDismiss,
  onFeatureTap,
}: WhatsNewModalProps) {
  const { t } = useTranslation('common');

  const categoryLabels: Record<WhatsNewItem['category'], string> = {
    feature: t('category_new', { defaultValue: 'NEW' }),
    improvement: t('category_improved', { defaultValue: 'IMPROVED' }),
    fix: t('category_fixed', { defaultValue: 'FIXED' }),
  };

  const latestVersion = getLatestVersion();
  const release = WHATS_NEW_RELEASES.find((r) => r.version === latestVersion);

  if (!release) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
      testID="whats-new-modal"
    >
      <View style={styles.overlay}>
        <View style={styles.card}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.versionBadge}>v{release.version}</Text>
            <Text style={styles.headline}>{t(`whats_new_headline_${release.version}`, { defaultValue: release.headline })}</Text>
            {daysInactive > 0 && (
              <Text style={styles.subtitle}>
                {t('reengagement_welcome_back', { count: daysInactive, defaultValue: "Welcome back! It's been {{count}} days." })}
              </Text>
            )}
          </View>

          {/* Feature cards */}
          <ScrollView
            style={styles.scrollArea}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {release.items.map((item, index) => (
              <Pressable
                key={`${item.title}-${index}`}
                style={({ pressed }) => [
                  styles.featureCard,
                  pressed && styles.featureCardPressed,
                ]}
                onPress={() => onFeatureTap(item.title, item.route)}
                testID={`whats-new-feature-${index}`}
                accessibilityRole="button"
                accessibilityLabel={`${item.title}: ${item.description}`}
              >
                <View style={styles.featureIconContainer}>
                  <Text style={styles.featureIcon}>{item.icon}</Text>
                </View>
                <View style={styles.featureTextContainer}>
                  <View style={styles.featureTitleRow}>
                    <Text style={styles.featureTitle}>{t(`whats_new_item_title_${index}`, { defaultValue: item.title })}</Text>
                    <View
                      style={[
                        styles.categoryBadge,
                        { backgroundColor: CATEGORY_COLORS[item.category] + '20' },
                      ]}
                    >
                      <Text
                        style={[
                          styles.categoryLabel,
                          { color: CATEGORY_COLORS[item.category] },
                        ]}
                      >
                        {categoryLabels[item.category]}
                      </Text>
                    </View>
                  </View>
                  <Text style={styles.featureDescription}>{t(`whats_new_item_desc_${index}`, { defaultValue: item.description })}</Text>
                </View>
              </Pressable>
            ))}
          </ScrollView>

          {/* Dismiss button */}
          <Pressable
            style={({ pressed }) => [
              styles.dismissButton,
              pressed && styles.dismissButtonPressed,
            ]}
            onPress={onDismiss}
            testID="whats-new-dismiss"
            accessibilityRole="button"
            accessibilityLabel={t('got_it_dismiss', { defaultValue: 'Got it, dismiss' })}
          >
            <Text style={styles.dismissText}>{t('got_it', { defaultValue: 'Got it' })}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
    backgroundColor: 'rgba(20, 10, 35, 0.95)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.3)',
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? {
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }
      : {}),
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
  },
  versionBadge: {
    fontSize: 12,
    fontWeight: '600',
    color: '#8B5CF6',
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 12,
  },
  headline: {
    fontSize: 22,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.6)',
    textAlign: 'center',
  },
  scrollArea: {
    flexShrink: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 10,
  },
  featureCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  featureCardPressed: {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
  featureIconContainer: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  featureIcon: {
    fontSize: 22,
  },
  featureTextContainer: {
    flex: 1,
  },
  featureTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  featureTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
    flexShrink: 1,
  },
  categoryBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  categoryLabel: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  featureDescription: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.6)',
    lineHeight: 18,
  },
  dismissButton: {
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 16,
    paddingVertical: 14,
    backgroundColor: '#8B5CF6',
    borderRadius: 14,
    alignItems: 'center',
    minHeight: 48,
    justifyContent: 'center',
  },
  dismissButtonPressed: {
    backgroundColor: '#7C3AED',
  },
  dismissText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
